import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ViewTypesNames } from "@redotech/redo-views-common/views";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";

export type RecordViewType =
  | ViewTypesNames.RETURNS
  | ViewTypesNames.CLAIMS
  | ViewTypesNames.WARRANTIES
  | ViewTypesNames.RECYCLE
  | ViewTypesNames.WITHDRAWALS;

/** Prefix of every counts query for one type; invalidate it after anything that moves records between views. */
export function recordViewCountsQueryKey(
  teamId: string,
  viewType: RecordViewType,
) {
  return ["record-view-counts", teamId, viewType] as const;
}

const NO_COUNTS: Record<string, number> = {};

/**
 * How many records each requested view holds, keyed by view id. Views
 * without a badge are counted only when `includeAll` is set.
 */
export function useRecordViewCounts({
  viewType,
  viewIds,
  includeAll = false,
}: {
  viewType: RecordViewType;
  viewIds: readonly string[];
  includeAll?: boolean;
}): { counts: Record<string, number>; isPending: boolean } {
  const team = useRequiredContext(TeamContext);
  const sortedViewIds = useMemo(() => [...viewIds].sort(), [viewIds]);

  const query = useRpcQuery(RedoMerchantRpcClientContext, {
    method: "getReturnViewNotificationCounts",
    args: { viewIds: sortedViewIds, type: viewType, includeAll },
    options: {
      queryKey: [
        ...recordViewCountsQueryKey(team._id, viewType),
        { viewIds: sortedViewIds, includeAll },
      ],
      enabled: sortedViewIds.length > 0,
      placeholderData: keepPreviousData,
    },
  });

  return {
    counts: query.data?.counts ?? NO_COUNTS,
    isPending: query.isPending,
  };
}
